import {Component, ChangeDetectionStrategy} from '@angular/core';
import {VisitContext} from '../../visit-context';
import {
  DeclarationType,
  FindVisits,
  Port,
  PreviousPort,
  VisitSummary,
  VisitSummarySortingProperty
} from '@portbase/bezoekschip-service-typescriptmodels';
import {v4 as uuid} from 'uuid';
import {sendQuery} from '../../../common/utils';
import moment from 'moment';
import {AppContext} from "../../../app-context";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import lodash from "lodash";
import {map} from "rxjs/operators";

@Component({
    selector: 'app-previous-ports',
    templateUrl: './previous-ports.component.html',
    styleUrls: ['./previous-ports.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
class PreviousPortsComponent {
  context = VisitContext;
  appContext = AppContext;

  relatedCrnFound: boolean;

  portInputFormatter = (port: Port) => port ? port.name + ' – ' + port.locationUnCode : "";

  searchPort = term => sendQuery("com.portbase.bezoekschip.common.api.visit.FindPorts", {term: term});

  searchVisits = term => term ? sendQuery("com.portbase.bezoekschip.common.api.visit.FindVisits", <FindVisits>{
    term: term,
    sortingProperty: VisitSummarySortingProperty.ARRIVAL_DATE,
    pagination: { from: 0, count: 20}
  }) : this.findRelatedVisits(this.getLastPreviousPort());

  addPreviousPort = () => this.context.visit.visitDeclaration.previousPorts.push(<PreviousPort>{
    id: uuid(),
    portFacilityVisits: []
  });

  visitFormatter = (visit: VisitSummary | string) => visit ? lodash.isString(visit) ? visit : visit.crn : null;

  onPreviousPortsDrop(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(
        this.context.visit.visitDeclaration.previousPorts,
        event.previousIndex,
        event.currentIndex,
      );
    }

    const activity = event.item.data;
    activity.arrival = null;
    activity.departure = null;
  }

  selectPreviousPort = (portVisit: PreviousPort, port: Port) => {
    const indexOfUpdate = VisitContext.visit.visitDeclaration.previousPorts.indexOf(portVisit);
    VisitContext.selectPreviousPort(portVisit, port);
    if (VisitContext.isVlissingenOrTerneuzenWithPilots()) {
      if (AppContext.isVlissingenOrTerneuzenVisit(port?.locationUnCode)) {
        if (indexOfUpdate === 0) {
          this.updateRelatedCrn(portVisit, port);
        }
      } else {
        VisitContext.visit.visitDeclaration.relatedCrn = null;
        this.relatedCrnFound = false;
      }
    }
  }

  deletePreviousPort = (portVisit: PreviousPort) => {
    const indexOfUpdate = VisitContext.visit.visitDeclaration.previousPorts.indexOf(portVisit);
    VisitContext.deletePreviousPort(portVisit);
    if (VisitContext.isVlissingenOrTerneuzenWithPilots() && indexOfUpdate === 0) {
      VisitContext.visit.visitDeclaration.relatedCrn = null;
      this.relatedCrnFound = false;
    }
  }

  findRelatedVisits = (portVisit: PreviousPort, port?: Port) => {
    return sendQuery("com.portbase.bezoekschip.common.api.visit.FindVisits", <FindVisits>{
      term: VisitContext.visit.vessel.name,
      sortingProperty: VisitSummarySortingProperty.ARRIVAL_DATE,
      dateTimeRange: {
        start: portVisit?.arrival,
        end: portVisit?.departure
      },
      pagination: {from: 0, count: 20},
      facetFilters: [{facetName: "portName", values: [(port || portVisit.port)?.name]}]
    }).pipe(map((visits: VisitSummary[]) => visits.filter(v => v.crn !== VisitContext.visit.crn)));
  };

  updateRelatedCrn = (portVisit: PreviousPort, port?: Port) => {
    if (portVisit) {
      this.findRelatedVisits(portVisit, port).subscribe((value: VisitSummary[]) => {
        if (value.length && value[0]) {
          VisitContext.visit.visitDeclaration.relatedCrn = VisitContext.visit.visitDeclaration.relatedCrn || value[0].crn;
          this.relatedCrnFound = true;
        }
      });
    }
  }

  getLastPreviousPort() {
    return VisitContext.visit.visitDeclaration.previousPorts[0];
  }

  get previousPortIsRelatedPort(): boolean {
    return AppContext.isVlissingenOrTerneuzenVisit(this.getLastPreviousPort()?.port?.locationUnCode)
      && VisitContext.isVlissingenOrTerneuzenWithPilots();
  }

  protected twoPortsNotChronological(index: number) {
    if (index === 0) {
      return false;
    }
    const port = this.context.visit.visitDeclaration.previousPorts[index];
    const date = port.departure ? port.departure : port.arrival;
    const nextPort = this.context.visit.visitDeclaration.previousPorts[index - 1]
    const nextDate = nextPort.arrival ? nextPort.arrival : nextPort.departure;
    return date && nextDate && moment(date).isAfter(moment(nextDate));
  }

  protected portNotChronological(port: PreviousPort) {
    return port.arrival && port.departure && moment(port.arrival).isAfter(moment(port.departure));
  }

  protected someFacilitiesAreOutsidePortVisit(port: PreviousPort) {
    if (!port.portFacilityVisits) {
      return false;
    }
    const arrivalMoment = moment(port.arrival);
    const departureMoment = moment(port.departure);
    let result = false;
    port.portFacilityVisits.forEach(facility => {
      if ((facility.arrivalDate && moment(facility.arrivalDate).isBefore(arrivalMoment, 'day'))
        || (facility.departureDate && departureMoment.isBefore(moment(facility.departureDate), 'day'))) {
        result = true;
      }
    });
    return result;
  }

  isEnabled = (): boolean => {
    return this.appContext.isAdmin() && !this.context.visit.cancelled && !(this.isDeclared())
  }

  private isDeclared(): boolean {
    const latestDeclaration = this.context.findLatestDeclaration(DeclarationType.VISIT);
    if (latestDeclaration) {
      return latestDeclaration.status === 'DECLARED';
    }
  }
}

export default PreviousPortsComponent
